
import React, { useState } from 'react';
import pkg from '../../package.json';
import { Icons } from '../ui/Icons';
import { ResetAppButton } from '../ui/ResetAppButton';
import type { LoginCredentials } from '../../types';

interface LoginProps {
    onLogin: (credentials: Omit<LoginCredentials, 'name'>, rememberMe: boolean) => Promise<void>;
    onGoogleLogin: () => Promise<void>;
    onForgotPassword: (email: string) => Promise<void>;
    onRegisterFirstAdmin: (credentials: LoginCredentials) => Promise<void>;
    onQuickSetupFirstAdmin?: () => Promise<void>;
    adminCount: number;
    propertyName: string;
}

const Login: React.FC<LoginProps> = ({ onLogin, onGoogleLogin, onForgotPassword, onRegisterFirstAdmin, onQuickSetupFirstAdmin, adminCount, propertyName }) => {
    const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);

    // If no admins exist, force registration of the first one
    const showRegister = adminCount === 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (showRegister) {
                await onRegisterFirstAdmin({ email, password, name, phone });
            } else {
                if (loginMethod === 'email') {
                    await onLogin({ email, password }, rememberMe);
                } else {
                    await onLogin({ phone, password }, rememberMe);
                }
            }
            // Reset attempts on success
            setFailedAttempts(0);
        } catch (error) {
            setFailedAttempts(prev => prev + 1);
            throw error; // Re-throw to let parent handle error reporting
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickSetup = async () => {
        if (onQuickSetupFirstAdmin) {
            setIsLoading(true);
            try {
                await onQuickSetupFirstAdmin();
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleForgotPasswordClick = async () => {
        if (!email) {
            alert('Please enter your email address first.');
            return;
        }
        setIsLoading(true);
        try {
            await onForgotPassword(email);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-6 sm:p-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                     <img src="/icon.svg" alt="Hotel Operations Hub" className="h-16 w-16 mx-auto mb-4" referrerPolicy="no-referrer" />
                    <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Hotel Operations Hub</h1>
                    <p className="mt-1 text-lg font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">{propertyName}</p>
                </div>
                
                <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 text-center mb-8">
                        {showRegister ? 'Setup First Administrator' : 'Sign In'}
                    </h2>

                    {!showRegister && (
                        <div className="flex bg-gray-100 dark:bg-slate-700 p-1 rounded-xl mb-8">
                            <button
                                type="button"
                                onClick={() => setLoginMethod('email')}
                                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                                    loginMethod === 'email'
                                        ? 'bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                Email
                            </button>
                            <button
                                type="button"
                                onClick={() => setLoginMethod('phone')}
                                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                                    loginMethod === 'phone'
                                        ? 'bg-white dark:bg-slate-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                Phone
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {showRegister && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        required
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        placeholder="+15141234567"
                                    />
                                </div>
                            </div>
                        )}
                        
                        {(loginMethod === 'email' || showRegister) ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="name@company.com"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                                <input
                                    type="tel"
                                    required
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    placeholder="+15141234567"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                placeholder="••••••••"
                            />
                        </div>

                        {!showRegister && (
                            <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Remember me</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={handleForgotPasswordClick}
                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <Icons.Loader className="w-5 h-5 animate-spin" />
                            ) : (
                                showRegister ? 'Create Admin Account' : 'Sign In'
                            )}
                        </button>

                        {showRegister && onQuickSetupFirstAdmin && (
                            <button
                                type="button"
                                onClick={handleQuickSetup}
                                disabled={isLoading}
                                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
                            >
                                <Icons.ShieldCheck className="w-5 h-5" />
                                Quick Setup (Default Admin)
                            </button>
                        )}

                        <div className="relative my-8">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white dark:bg-slate-800 text-gray-500">Or continue with</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onGoogleLogin}
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl transition-all flex items-center justify-center gap-3"
                        >
                            <Icons.Google className="w-5 h-5" />
                            Sign in with Google
                        </button>

                        {(failedAttempts >= 2 || process.env.NODE_ENV === 'development') && (
                            <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-500">
                                <ResetAppButton variant="button" className="w-full justify-center py-2 px-4 border border-amber-200 dark:border-amber-900/30 bg-amber-50/50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/20" />
                                <p className="text-[10px] text-center text-gray-400 mt-2">Use this if login or data seems incorrect</p>
                            </div>
                        )}
                    </form>
                </div>

                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400 flex flex-col items-center gap-4">
                    <ResetAppButton variant="link" />
                    <p className="text-xs opacity-50">v{pkg.version}</p>
                </div>
            </div>
        </div>
    );
};

export default Login;